import { TeamBadge } from "../common/TeamBadge";
import { tr } from "../../i18n";
import { PalloniGol, type ProgressoGol } from "./PalloniGol";

/** Christian 2026-11-03: risultato provvisorio della giornata in corso. */
export interface PunteggioLive {
  golCasa: number;
  golTrasferta: number;
  puntiCasa: number;
  puntiTrasferta: number;
  progressoCasa: ProgressoGol | null;
  progressoTrasferta: ProgressoGol | null;
}

const fmtPunti = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 });

interface TeamMini {
  nome: string;
  /** Andrea 2026-05-20 — logo personalizzato (data-URL). Se null → iniziali. */
  logo_data_url?: string | null;
}

interface MatchCardProps {
  competizione: string;
  data: string;
  homeTeam: TeamMini;
  awayTeam: TeamMini;
  /** CTA in fondo (es. "FORMAZIONE NON SCHIERATA"). Se variant=warning, CTA in rosso. */
  cta?: { label: string; variant?: "default" | "warning"; onClick?: () => void };
  /** Se c'e': gol al posto del VS, fantapunti e palloni sotto le squadre. */
  live?: PunteggioLive | null;
}

export function MatchCard({ competizione, data, homeTeam, awayTeam, cta, live }: MatchCardProps) {
  return (
    <div className="bg-bg-card border border-primary/20 rounded-card p-4">
      {/* Meta: competizione + data */}
      <div className="flex justify-between items-center pb-3 mb-3 border-b border-dashed border-primary/30">
        <span className="font-mono text-[10px] uppercase tracking-wider text-accent font-bold">
          ★ {competizione}
        </span>
        <span className="text-xs text-fg-muted">{data}</span>
      </div>

      {/* Squadre */}
      {/* Andrea 2026-10-30: stemmi in riga 1, nomi in riga 2 (un nome
          lungo non sposta lo stemma). */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-x-3 gap-y-2">
        <div className="flex justify-center">
          <TeamBadge
            nome={homeTeam.nome}
            logoDataUrl={homeTeam.logo_data_url}
            size={48}
            variant="home"
          />
        </div>
        {live ? (
          <div className="row-span-2 flex flex-col items-center leading-none">
            <div className="font-display text-3xl tracking-wider text-fg tabular-nums">
              {live.golCasa}
              <span className="text-fg-muted mx-1">–</span>
              {live.golTrasferta}
            </div>
            <div className="mt-1 text-[9px] font-mono uppercase tracking-wider text-danger">
              {tr("provvisorio")}
            </div>
          </div>
        ) : (
          <div className="row-span-2 font-display text-xl tracking-wider text-fg-muted">VS</div>
        )}
        <div className="flex justify-center">
          <TeamBadge
            nome={awayTeam.nome}
            logoDataUrl={awayTeam.logo_data_url}
            size={48}
            variant="away"
          />
        </div>
        <div className="text-center min-w-0 self-start text-xs font-bold text-fg leading-tight break-words">
          {homeTeam.nome}
        </div>
        <div className="text-center min-w-0 self-start text-xs font-bold text-fg leading-tight break-words">
          {awayTeam.nome}
        </div>
        {live && (
          <>
            <LatoLive punti={live.puntiCasa} progresso={live.progressoCasa} />
            <div />
            <LatoLive punti={live.puntiTrasferta} progresso={live.progressoTrasferta} />
          </>
        )}
      </div>

      {cta && (
        <button
          type="button"
          onClick={cta.onClick}
          className={`mt-4 w-full py-2.5 rounded-card font-bold text-sm tracking-wider transition ${
            cta.variant === "warning"
              ? "bg-danger text-white hover:bg-danger/90"
              : "bg-primary text-fg hover:bg-primary-soft"
          }`}
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}

function LatoLive({ punti, progresso }: { punti: number; progresso: ProgressoGol | null }) {
  return (
    <div className="flex flex-col items-center gap-1 min-w-0">
      <div className="font-display text-2xl leading-none text-accent tabular-nums">
        {fmtPunti(punti)}
        <span className="ml-0.5 text-[10px] font-mono text-fg-muted">{tr("pt")}</span>
      </div>
      {progresso && <PalloniGol progresso={progresso} />}
    </div>
  );
}
